import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Zap, TrendingUp, TrendingDown, BarChart3, Activity } from "lucide-react";
import DataBadge from "./DataBadge";

function fmtPrice(p) { return p ? Number(p).toFixed(2) : "—"; }
function fmtPct(p) { if (p == null) return "—"; return `${p >= 0 ? "+" : ""}${Number(p).toFixed(2)}%`; }

function Tile({ icon: Icon, label, item, accent = "var(--bull)", onSelect }) {
  if (!item) {
    return (
      <div className="card-surface p-4">
        <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-white/50" /><div className="label-mono">{label}</div></div>
        <div className="text-white/40 text-xs mt-3">No data yet</div>
      </div>
    );
  }
  const isBull = item.direction !== "bearish";
  return (
    <div
      onClick={() => onSelect && onSelect(item.ticker)}
      className="card-surface glass-hover p-4 cursor-pointer relative overflow-hidden"
      data-testid={`leaderboard-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="absolute inset-0 pointer-events-none opacity-50"
           style={{ background: `radial-gradient(circle at 100% 0%, ${accent}25, transparent 60%)` }} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5" style={{ color: accent }} /><div className="label-mono">{label}</div></div>
          <span className={`signal-pill ${isBull ? "bull" : "bear"}`}>{isBull ? "BULL" : "BEAR"}</span>
        </div>
        <div className="flex items-baseline gap-2 mt-3">
          <div className="text-2xl text-white font-semibold tracking-tight">{item.ticker}</div>
          <div className={`font-mono text-sm ${item.change_pct >= 0 ? "text-bull" : "text-bear"}`}>{fmtPct(item.change_pct)}</div>
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="text-white/45 text-xs truncate max-w-[150px]">{item.name}</div>
          <div className="font-mono text-white text-sm tabular-nums">${fmtPrice(item.price)}</div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
          <div className="text-[10px] font-mono uppercase tracking-wider text-white/50">RSI {item.rsi?.toFixed(1)} · RVOL {item.rvol?.toFixed(2)}</div>
          <div className="text-white font-mono text-sm tabular-nums">{item.score}</div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ value, label, accent = "var(--bull)" }) {
  return (
    <div className="card-surface p-4">
      <div className="label-mono">{label}</div>
      <div className="font-mono text-2xl text-white font-semibold tabular-nums mt-1 tracking-tight" style={{ textShadow: `0 0 16px ${accent}55` }}>{value.toLocaleString()}</div>
    </div>
  );
}

export default function LiveBoard({ onSelectTicker }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/api/leaderboard");
      setBoard(data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !board) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="card-surface p-4 animate-pulse">
            <div className="h-3 w-20 bg-white/5 rounded mb-3" />
            <div className="h-8 w-24 bg-white/5 rounded" />
            <div className="h-3 w-32 bg-white/5 rounded mt-3" />
          </div>
        ))}
      </div>
    );
  }
  if (!board) return null;

  const m = board.metrics || {};
  const status = ["live", "delayed", "stale"].includes(board.data_source)
    ? board.data_source
    : "delayed";

  return (
    <div className="space-y-4" data-testid="live-board">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Activity className="w-4 h-4 text-[var(--bull)]" />
          <div>
            <div className="label-mono">Live Board</div>
            <h2 className="text-xl sm:text-2xl text-white font-semibold tracking-tight mt-0.5">FlowFinder market pulse</h2>
          </div>
        </div>
        <DataBadge status={status} />
      </div>

      {/* metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard value={m.scans_today || 0} label="Scans today" />
        <MetricCard value={m.active_users || 0} label="Active users" accent="#6c5ce7" />
        <MetricCard value={m.bullish_setups_24h || 0} label="Bullish · 24h" accent="var(--bull)" />
        <MetricCard value={m.bearish_setups_24h || 0} label="Bearish · 24h" accent="var(--bear)" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <Tile icon={TrendingUp} label="Strongest Bullish" item={board.strongest_bullish} accent="var(--bull)" onSelect={onSelectTicker} />
        <Tile icon={TrendingDown} label="Strongest Bearish" item={board.strongest_bearish} accent="var(--bear)" onSelect={onSelectTicker} />
        <Tile icon={Zap} label="Biggest Volume Spike" item={board.biggest_volume} accent="#ffd166" onSelect={onSelectTicker} />
        <Tile icon={BarChart3} label="Hottest ETF" item={board.hottest_etf} accent="#6c5ce7" onSelect={onSelectTicker} />
        <Tile icon={Flame} label="Top Momentum Stock" item={board.top_momentum} accent="var(--bull)" onSelect={onSelectTicker} />
      </div>
    </div>
  );
}
